import type { DesignAsset, DesignShape } from '../../types/design'
import type { AutomationAction, AutomationTrigger } from '../../types/automation'
import type { MaterializationAdapter, MaterializationContext, MaterializationResult } from './index'

export type AutomationDraft = {
  workspaceId: string
  sourceDesignAssetId: string
  name: string
  description: string
  triggers: AutomationTrigger[]
  then: AutomationAction[]
  else?: AutomationAction[]
  actor: { id: string; name: string }
}

const trim = (raw: string | undefined, fallback: string): string => {
  const value = (raw ?? '').trim()
  return value.length ? value.slice(0, 200) : fallback
}

const newActionId = (prefix: string, index: number) =>
  `a_${prefix.toLowerCase()}_${Date.now().toString(36)}_${index.toString(36)}`

const buildTrigger = (): AutomationTrigger => ({
  type: 'NODE_CREATED',
  config: { scope: 'WORKSPACE', nodeKind: 'ANY' },
})

const buildAction = (shape: DesignShape, index: number, branch: 'THEN' | 'ELSE'): AutomationAction => ({
  id: newActionId(branch, index),
  type: 'NOTIFY_USER',
  config: {
    title: trim(shape.text ?? shape.name, `Automation step ${index + 1}`),
    body: 'Draft materialized from a Bound design. Complete configuration in Automate.',
  },
})

const isTriggerShape = (shape: DesignShape): boolean =>
  shape.semanticRole === 'AUTOMATION_TRIGGER'

const isActionShape = (shape: DesignShape): boolean =>
  shape.semanticRole === 'AUTOMATION_ACTION'

const isBranchShape = (shape: DesignShape): boolean =>
  shape.semanticRole === 'AUTOMATION_BRANCH'

const sortByCanvasOrder = (shapes: DesignShape[]) =>
  shapes.slice().sort((a, b) => a.position.y - b.position.y || a.position.x - b.position.x)

const runAutomationAdapter = (
  asset: DesignAsset,
  ctx: MaterializationContext,
): MaterializationResult<AutomationDraft> => {
  const warnings: string[] = []

  const triggerShapes = sortByCanvasOrder(asset.shapes.filter(isTriggerShape))
  const actionShapes = sortByCanvasOrder(asset.shapes.filter(isActionShape))
  const branchShapes = sortByCanvasOrder(asset.shapes.filter(isBranchShape))

  if (triggerShapes.length === 0 && actionShapes.length === 0) {
    return {
      ok: false,
      errors: [
        'This design has no shapes tagged as AUTOMATION_TRIGGER or AUTOMATION_ACTION. Assign roles in the property panel before materializing an automation.',
      ],
    }
  }

  if (triggerShapes.length === 0) {
    warnings.push('No AUTOMATION_TRIGGER shape found — a NODE_CREATED trigger was added as a placeholder. Configure it in Automate.')
  }
  if (triggerShapes.length > 1) {
    warnings.push(`${triggerShapes.length} trigger shapes detected — all mapped to NODE_CREATED placeholders. Adjust types in Automate.`)
  }

  const triggers: AutomationTrigger[] = triggerShapes.length > 0
    ? triggerShapes.map(() => buildTrigger())
    : [buildTrigger()]

  const thenActions: AutomationAction[] = actionShapes.map((shape, index) => buildAction(shape, index, 'THEN'))
  const elseActions: AutomationAction[] = branchShapes.map((shape, index) => buildAction(shape, index, 'ELSE'))

  if (thenActions.length === 0 && elseActions.length === 0) {
    warnings.push('No AUTOMATION_ACTION shapes found — a placeholder NOTIFY_USER action was added so the graph passes validation.')
    thenActions.push({
      id: newActionId('placeholder', 0),
      type: 'NOTIFY_USER',
      config: {
        title: 'Automation fired',
        body: 'Draft materialized from a Bound design. Complete configuration in Automate.',
      },
    })
  }

  if (branchShapes.length > 0) {
    warnings.push(`${branchShapes.length} branch shape${branchShapes.length === 1 ? '' : 's'} mapped to ELSE actions — add IF conditions in Automate to route between THEN and ELSE.`)
  }

  const draft: AutomationDraft = {
    workspaceId: ctx.workspaceId,
    sourceDesignAssetId: asset.id,
    name: trim(asset.title, 'Untitled automation'),
    description: `Materialized from Bound design "${asset.title || 'Untitled design'}". Starts PAUSED so you can review triggers and actions.`,
    triggers,
    then: thenActions,
    else: elseActions.length > 0 ? elseActions : undefined,
    actor: ctx.actor,
  }

  return { ok: true, draft, warnings }
}

export const automationAdapter: MaterializationAdapter<AutomationDraft> = {
  kind: 'AUTOMATION_MAP',
  supportedRoles: ['AUTOMATION_TRIGGER', 'AUTOMATION_ACTION', 'AUTOMATION_BRANCH'],
  run: runAutomationAdapter,
}
